"use client";

import { useState } from "react";

type IntegrationCardProps = {
    abbr: string;
    title: string;
    desc: string;
};

/** Карточка направления: мобильная flip-версия + десктопная карточка с водяным знаком. */
export function IntegrationCard({ abbr, title, desc }: IntegrationCardProps) {
    const [flipped, setFlipped] = useState(false);

    return (
        <div className="relative h-full">
            {/* Мобильная версия: flip-карточка */}
            <div className="sm:hidden h-full">
                <button
                    type="button"
                    onClick={() => setFlipped((prev) => !prev)}
                    aria-label={`${title} ${abbr} ${desc}`}
                    className="relative grid h-full min-h-[96px] w-full cursor-pointer overflow-hidden border border-white/10 bg-white/5 p-3 text-left"
                    style={{ perspective: "1000px", WebkitPerspective: "1000px" }}
                >
                    {/* Обе грани всегда в DOM: высота кнопки постоянна, грид не пересчитывается */}
                    <div
                        className="col-start-1 row-start-1 flex h-full flex-col items-center justify-center text-center"
                        style={{
                            transform: flipped ? "rotateY(90deg)" : "rotateY(0deg)",
                            opacity: flipped ? 0 : 1,
                            transition: "transform 0.35s ease-out, opacity 0.35s ease-out",
                        }}
                        aria-hidden={flipped}
                    >
                        <h3 className="relative z-10 text-[13px] font-semibold leading-snug text-white/85">{title}</h3>
                        <span className="mt-2 text-lg font-bold leading-none text-white/60 select-none" aria-hidden="true">
                            {abbr}
                        </span>
                    </div>
                    <div
                        className="col-start-1 row-start-1 flex h-full flex-col items-center justify-center text-center"
                        style={{
                            transform: flipped ? "rotateY(0deg)" : "rotateY(-90deg)",
                            opacity: flipped ? 1 : 0,
                            transition: "transform 0.35s ease-out, opacity 0.35s ease-out",
                        }}
                        aria-hidden={!flipped}
                    >
                        <p className="relative z-10 text-[11px] text-white/70 leading-relaxed">{desc}</p>
                    </div>
                </button>
            </div>
            {/* Планшет и десктоп: обычная карточка */}
            <div className="relative hidden h-full flex-col border border-white/10 bg-white/5 p-4 transition-colors duration-300 hover:border-white/25 hover:bg-white/10 sm:flex">
                <h3 className="relative z-10 text-sm font-semibold leading-snug text-white/85">{title}</h3>
                <p className="relative z-10 mt-1 text-xs text-white/70 leading-relaxed">{desc}</p>
                <span
                    className="pointer-events-none absolute right-2 bottom-1 z-0 text-4xl font-bold leading-none text-white/45 select-none"
                    aria-hidden="true"
                >
                    {abbr}
                </span>
            </div>
        </div>
    );
}

type BenefitCardProps = {
    number: string;
    title: string;
    text: string;
};

/** Карточка преимущества с номером-водяным знаком. */
export function BenefitCard({ number, title, text }: BenefitCardProps) {
    return (
        <div className="relative flex flex-col border border-white/10 bg-white/5 p-4 transition-colors duration-300 hover:border-white/25 hover:bg-white/10 max-md:p-3">
            <h3 className="relative z-10 text-sm font-semibold text-white/85">{title}</h3>
            <p className="relative z-10 mt-1 text-xs text-white/70 leading-relaxed">{text}</p>
            <span
                className="pointer-events-none absolute right-2 bottom-1 z-0 text-4xl font-bold leading-none text-white/45 select-none max-md:text-3xl"
                aria-hidden="true"
            >
                {number}
            </span>
        </div>
    );
}
